import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getSalesSummary, getSalesSummaryAccounting, getBusinessUnitSalesSection, getPosStock } from "../services/salesSummaryApi";
import type { SalesSummaryFilters } from "../services/salesSummaryApi";
import { normalizeError } from "@/shared/errors/normalizeError";

export const salesSummaryKeys = {
  all: ["sales-summary"] as const,
  posStock: (pageNumber: number, pageSize: number) => ["sales-summary", "pos-stock", pageNumber, pageSize] as const,
  summary: (scope: string, from?: string, to?: string, filters: SalesSummaryFilters = {}) =>
    ["sales-summary", scope, from, to, filters] as const,
  accounting: (scope: string, from?: string, to?: string) =>
    ["sales-summary", "accounting", scope, from, to] as const,
  section: (section: "summary" | "regions" | "items", scope: string, from?: string, to?: string, filters: SalesSummaryFilters = {}) =>
    ["sales-summary", "business-unit", section, scope, from, to, filters] as const,
};

export function usePosStock(pageNumber: number, pageSize: number) {
  return useQuery({
    queryKey: salesSummaryKeys.posStock(pageNumber, pageSize),
    queryFn: ({ signal }) => getPosStock(pageNumber, pageSize, signal),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export function useSalesSummary(scope: string, from?: string, to?: string, filters: SalesSummaryFilters = {}, enabled = true) {
  return useQuery({
    queryKey: salesSummaryKeys.summary(scope, from, to, filters),
    queryFn: () => getSalesSummary(scope, from, to, filters),
    enabled,
  });
}

export function useBusinessUnitSalesSection(
  section: "summary" | "regions" | "items",
  scope: string, from?: string, to?: string, filters: SalesSummaryFilters = {}, enabled = false,
) {
  return useQuery({
    queryKey: salesSummaryKeys.section(section, scope, from, to, filters),
    queryFn: () => getBusinessUnitSalesSection(section, scope, from, to, filters),
    enabled,
    retry: (failureCount, error) => normalizeError(error).httpStatus !== 409 && failureCount < 2,
  });
}

export function useSalesSummaryAccounting(scope: string, from?: string, to?: string, enabled = false) {
  return useQuery({
    queryKey: salesSummaryKeys.accounting(scope, from, to),
    queryFn: () => getSalesSummaryAccounting(scope, from, to),
    enabled,
  });
}
